export const DebounceType = {
    /** The function will be called after idle for waitMilliseconds. */ TrailingEdge: 'TrailingEdge',
    /** The function will be called once immediately and then sit idle until the wait period has elapsed after an action. */ LeadingEdge: 'LeadingEdge',
    /**
   * The function will run immediately if there's not a timer waiting. If called again while the timer is going,
   * the last function call will be executed when the timer completes.
   */ LeadingAndTrailingEdges: 'LeadingAndTrailingEdges'
};
/**
 * @deprecated Use `getDebouncedFunction` instead.
 * {@link getDebouncedFunction} is a name that provides more context in newly written code.
 * Leaving the old export as well for backwards compatibility.
 */ export const debounce = getDebouncedFunction;
/**
 * This utility wraps {@link func} in a function that will perform debouncing behavior.
 * @param func
 * The function to be wrapped in debounce behavior.
 * @param waitMilliseconds
 * The minimum elapsed time before the wrapped function will be called.
 * @param debounceType
 * Defaults to {@link DebounceType.TrailingEdge}. This determines if the function should be
 * called on the leading edge, the trailing edge or both. See comments in {@link DebounceType} for details.
 *
 * @example
 * const rawFunction = (bar: string): string => bar + '!';
 *
 * const debouncedRawFunction = getDebouncedFunction((bar: string) => rawFunc(bar), 250);
 *
 * let result = '';
 * result = debouncedRawFunction('the');
 * // Wait 250ms
 * expect(result).toBe('the!');
 *
 * result = '';
 * result = debouncedRawFunction('the');
 * result = debouncedRawFunction('the best thing');
 * result = debouncedRawFunction('the best thing ever');
 * expect(result).toBe('');
 * // Wait 250ms
 * expect(result).toBe('the best thing ever!');
 */ // eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getDebouncedFunction(func, waitMilliseconds, debounceType = DebounceType.TrailingEdge) {
    let waitingForTimer = false;
    let endTime = 0;
    let lastThis; // Keep track of last 'this'
    let lastArgs; // Keep track of last args
    let callOnTrailingEdge = false;
    return function(...args) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        lastThis = this;
        lastArgs = args;
        endTime = waitMilliseconds + Date.now();
        callOnTrailingEdge = debounceType !== DebounceType.LeadingEdge;
        if (waitingForTimer) {
            return;
        }
        const callOnLeadingEdge = debounceType !== DebounceType.TrailingEdge;
        if (callOnLeadingEdge) {
            // When DebounceType is LeadingAndTrailingEdges, we want to call on trailing edge IFF we get a second call
            // so for the first call set callOnTrailingEdge to false
            callOnTrailingEdge = false;
            func.apply(lastThis, lastArgs);
        }
        const doLater = ()=>{
            waitingForTimer = false;
            const now = Date.now();
            if (endTime > now) {
                waitingForTimer = true;
                setTimeout(doLater, endTime - now);
            } else if (callOnTrailingEdge) {
                func.apply(lastThis, lastArgs);
            }
        };
        waitingForTimer = true;
        setTimeout(doLater, waitMilliseconds);
    };
}
